import type { GhsPictogram } from "./questions";

/**
 * Simplified, monochrome GHS pictograms rendered inline with SVG so we don't
 * ship raster assets. These are educational stand-ins for the official red-bordered
 * diamonds - recognisable enough that students can practise identifying them, but
 * styled to match the app's accent system.
 *
 * The standard GHS frame is a red diamond on white. We render a rotated square
 * with the design system's destructive token for the border so the symbol still
 * reads correctly in both light and dark modes.
 */
export default function GhsPictogramIcon({
  symbol,
  size = 96,
}: {
  symbol: GhsPictogram;
  size?: number;
}) {
  return (
    <div
      className="relative inline-flex items-center justify-center"
      style={{ width: size, height: size }}
      role="img"
      aria-label={`GHS hazard pictogram: ${symbol.replace(/-/g, " ")}`}
    >
      {/* Rotated diamond frame */}
      <div
        className="absolute inset-2 rotate-45 border-[3px] border-destructive bg-background"
        aria-hidden="true"
      />
      <svg
        viewBox="0 0 64 64"
        width={size * 0.55}
        height={size * 0.55}
        className="relative text-foreground"
        aria-hidden="true"
      >
        {renderSymbol(symbol)}
      </svg>
    </div>
  );
}

function renderSymbol(symbol: GhsPictogram) {
  switch (symbol) {
    case "flame":
      return (
        <path
          d="M32 4 C 26 18 18 22 18 36 a14 14 0 0 0 28 0 c 0 -8 -6 -10 -8 -18 c -2 6 -6 8 -6 14 c 0 -10 4 -16 0 -28 z"
          fill="currentColor"
        />
      );
    case "exclamation":
      return (
        <>
          <rect x="28" y="10" width="8" height="28" rx="2" fill="currentColor" />
          <circle cx="32" cy="50" r="5" fill="currentColor" />
        </>
      );
    case "skull":
      return (
        <>
          {/* skull */}
          <ellipse cx="32" cy="26" rx="16" ry="14" fill="currentColor" />
          <rect x="24" y="36" width="16" height="8" rx="2" fill="currentColor" />
          <circle cx="26" cy="26" r="3" className="fill-background" />
          <circle cx="38" cy="26" r="3" className="fill-background" />
          {/* crossbones */}
          <rect
            x="6"
            y="48"
            width="52"
            height="4"
            rx="2"
            fill="currentColor"
            transform="rotate(15 32 50)"
          />
          <rect
            x="6"
            y="48"
            width="52"
            height="4"
            rx="2"
            fill="currentColor"
            transform="rotate(-15 32 50)"
          />
        </>
      );
    case "corrosion":
      return (
        <>
          {/* two flasks pouring */}
          <path d="M10 8 v10 l-4 12 h12 l-4 -12 v -10 z" fill="currentColor" />
          <path d="M50 8 v10 l4 12 h-12 l4 -12 v -10 z" fill="currentColor" />
          {/* drip lines */}
          <path d="M14 30 q -2 4 0 8" stroke="currentColor" strokeWidth="2" fill="none" />
          <path d="M50 30 q 2 4 0 8" stroke="currentColor" strokeWidth="2" fill="none" />
          {/* hand / surface */}
          <path d="M6 50 h24 l4 -4 h12 v8 H6 z" fill="currentColor" />
          {/* corrosion bite */}
          <path d="M28 48 l4 -2 l3 2 l3 -2 v4 h-10 z" className="fill-background" />
        </>
      );
    case "health-hazard":
      return (
        <>
          {/* torso silhouette */}
          <path
            d="M32 6 a6 6 0 1 1 0 12 a6 6 0 1 1 0 -12 z M22 22 h20 v20 h-20 z"
            fill="currentColor"
          />
          {/* starburst on chest */}
          <path
            d="M32 26 l3 6 l6 -1 l-4 5 l4 5 l-6 -1 l-3 6 l-3 -6 l-6 1 l4 -5 l-4 -5 l6 1 z"
            className="fill-background"
          />
        </>
      );
    case "environment":
      return (
        <>
          {/* dead tree */}
          <rect x="14" y="14" width="3" height="30" fill="currentColor" />
          <path d="M15 18 l-6 -4 M15 22 l-6 0 M15 26 l-6 4" stroke="currentColor" strokeWidth="2" />
          {/* water line */}
          <path d="M2 46 q 16 -6 32 0 t 28 0 v6 H2 z" fill="currentColor" />
          {/* dead fish */}
          <path
            d="M40 34 q 10 -4 16 0 q -6 4 -16 0 z M56 32 l4 -3 v6 z"
            fill="currentColor"
          />
          <circle cx="44" cy="33" r="1.2" className="fill-background" />
        </>
      );
    case "exploding-bomb":
      return (
        <>
          <circle cx="32" cy="38" r="14" fill="currentColor" />
          <path
            d="M32 8 l4 12 l10 -6 l-6 12 l12 2 l-12 4 l6 12 l-10 -6 l-4 12 l-4 -12 l-10 6 l6 -12 l-12 -4 l12 -2 l-6 -12 l10 6 z"
            fill="currentColor"
          />
        </>
      );
    case "flame-over-circle":
      return (
        <>
          <circle cx="32" cy="48" r="6" stroke="currentColor" strokeWidth="3" fill="none" />
          <line x1="20" y1="50" x2="44" y2="50" stroke="currentColor" strokeWidth="3" />
          <path
            d="M32 6 C 26 18 20 22 20 32 a12 12 0 0 0 24 0 c 0 -8 -6 -10 -8 -16 c -2 4 -4 6 -4 10 c 0 -8 4 -12 0 -20 z"
            fill="currentColor"
          />
        </>
      );
    case "gas-cylinder":
      return (
        <>
          <rect x="22" y="10" width="20" height="44" rx="4" fill="currentColor" />
          <rect x="28" y="6" width="8" height="6" fill="currentColor" />
          <rect x="26" y="22" width="12" height="4" className="fill-background" />
        </>
      );
  }
}